import type { DashboardId, FormName, FormRecord } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, funnel, lines, list, type Rect } from '../charts.ts';
import { formDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

const RANK: Record<FormRecord['status'], number> = {
  'Not Started': 0,
  Entered: 1,
  Verified: 2,
  Signed: 3,
  Locked: 4,
};

/**
 * What selecting a funnel stage opens: the pages that have not reached that stage yet, i.e.
 * the ones sitting at the step before it. "Expected" lists every page by its status.
 */
export const STAGE_GAP: Record<string, { below: number; title: string; hint: string }> = {
  Expected: { below: 5, title: 'All pages by status', hint: 'Select to list every page by status' },
  Entered: {
    below: 1,
    title: 'Pages not entered yet',
    hint: 'Select to list the pages not entered',
  },
  Verified: {
    below: 2,
    title: 'Entered, not verified (SDV pending)',
    hint: 'Select to list the pages awaiting SDV',
  },
  Signed: {
    below: 3,
    title: 'Verified, not signed',
    hint: 'Select to list the pages awaiting signature',
  },
  Locked: { below: 4, title: 'Signed, not locked', hint: 'Select to list the pages awaiting lock' },
};

/** Data Cleaning: clean %, SDV, signed, locked; funnel per form; progress over weeks; form or stage -> pages. */
export class DataCleaningDashboard extends Dashboard {
  readonly id: DashboardId = 'DataCleaning';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('page:')) {
      const f = this.view.form(segment.slice(5));
      return f ? `${f.subjectId} ${f.visitCode}` : segment;
    }
    if (segment.startsWith('stage:')) {
      const [stage, form] = segment.slice(6).split('|');
      return `${STAGE_GAP[stage!]?.title ?? stage}${form ? ` · ${form}` : ''}`;
    }
    return segment.replace(/^form:/, '');
  }

  /** The pages behind a funnel stage, optionally for one form. */
  private stagePages(stage: string, form: string | undefined): FormRecord[] {
    const gap = STAGE_GAP[stage];
    const forms = this.view.forms(this.filters).filter((f) => !form || f.form === form);
    if (!gap) return [];
    const pages = gap.below >= 5 ? forms : forms.filter((f) => RANK[f.status] === gap.below - 1);
    return pages.sort(
      (a, b) => RANK[a.status] - RANK[b.status] || a.dueDate.localeCompare(b.dueDate),
    );
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [form, page] = this.path;
    if (form && page) {
      const f = this.view.form(page.slice(5));
      if (f) {
        this.detail = formDetail(this.view, f);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const forms = this.view.forms(this.filters);
    if (form?.startsWith('stage:')) {
      const [stage, formName] = form.slice(6).split('|');
      const items = this.stagePages(stage!, formName || undefined);
      const rows = list(
        ctx,
        ['Subject', 'Form', 'Visit', 'Status', 'Due', 'Last step'],
        items.map((f) => ({
          cells: [
            f.subjectId,
            f.form,
            f.visitCode,
            f.status,
            `${f.dueDate}${f.overdue ? ' (overdue)' : ''}`,
            f.lockedAt ?? f.signedAt ?? f.verifiedAt ?? f.enteredAt ?? '-',
          ],
          flag: f.overdue ? 'x' : undefined,
          colour: PALETTE.amber,
        })),
        area,
        {
          title: `${STAGE_GAP[stage!]?.title ?? stage}${formName ? ` · ${formName}` : ' · all forms'} · ${items.length} pages`,
          widths: [0.15, 0.27, 0.11, 0.14, 0.18, 0.15],
          hoveredIndex: hoveredRow(panel, 'row:'),
          rowH: 20,
          emptyText: 'No pages at this step for the current filter',
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `page:${items[i]!.id}` },
          tooltip: [
            `${items[i]!.subjectId} · ${items[i]!.form} · ${items[i]!.visitCode}`,
            'Open the CRF page',
          ],
        }),
      );
      return;
    }
    if (form) {
      const name = form.slice(5) as FormName;
      const items = forms
        .filter(
          (f) =>
            f.form === name &&
            (f.status === 'Not Started' || (f.status === 'Entered' && f.missingFields.length > 0)),
        )
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      const rows = list(
        ctx,
        ['Subject', 'Visit', 'Status', 'Missing fields', 'Due'],
        items.map((f) => ({
          cells: [
            f.subjectId,
            f.visitCode,
            f.status,
            f.status === 'Not Started' ? 'not entered' : f.missingFields.join(', '),
            `${f.dueDate}${f.overdue ? ' (overdue)' : ''}`,
          ],
          flag: f.overdue ? 'x' : undefined,
          colour: PALETTE.amber,
        })),
        area,
        {
          title: `${name} · ${items.length} pages needing attention`,
          widths: [0.16, 0.12, 0.16, 0.38, 0.18],
          hoveredIndex: hoveredRow(panel, 'row:'),
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `page:${items[i]!.id}` },
          tooltip: [`${items[i]!.subjectId} · ${items[i]!.visitCode}`, 'Open the CRF page'],
        }),
      );
      return;
    }
    const atLeast = (s: FormRecord['status']) =>
      forms.filter((f) => RANK[f.status] >= RANK[s]).length;
    const clean = forms.filter(
      (f) => f.status !== 'Not Started' && f.missingFields.length === 0,
    ).length;
    const pctOf = (n: number) =>
      forms.length ? `${Math.round((n / forms.length) * 1000) / 10}%` : '—';
    const lastEntry = forms
      .map((f) => f.enteredAt)
      .filter((d): d is string => !!d)
      .sort()
      .pop();
    const daysSince = lastEntry
      ? Math.round((Date.parse(this.view.file.anchorDate) - Date.parse(lastEntry)) / 86_400_000)
      : null;
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'Clean',
          value: pctOf(clean),
          colour: PALETTE.teal,
          sub: 'entered, no missing fields',
        },
        { label: 'SDV (verified)', value: pctOf(atLeast('Verified')) },
        { label: 'Signed', value: pctOf(atLeast('Signed')) },
        { label: 'Locked', value: pctOf(atLeast('Locked')), colour: PALETTE.forest },
        { label: 'Since last entry', value: daysSince === null ? '—' : `${daysSince} d` },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const left: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.5, h: area.h - 88 };
    const names = this.view.file.study.forms.map((f) => f.name);
    const hoveredForm = hoveredRow(panel, 'form:');
    const perForm = names.map((n) => {
      const fs = forms.filter((f) => f.form === n);
      return {
        name: n,
        total: fs.length,
        entered: fs.filter((f) => RANK[f.status] >= 1).length,
        verified: fs.filter((f) => RANK[f.status] >= 2).length,
        signed: fs.filter((f) => RANK[f.status] >= 3).length,
        locked: fs.filter((f) => f.status === 'Locked').length,
        attention: fs.filter(
          (f) =>
            f.status === 'Not Started' || (f.status === 'Entered' && f.missingFields.length > 0),
        ).length,
      };
    });
    const selected = perForm[hoveredForm >= 0 ? hoveredForm : 0];
    const stages = selected
      ? [
          { label: 'Expected', value: selected.total },
          { label: 'Entered', value: selected.entered },
          { label: 'Verified', value: selected.verified },
          { label: 'Signed', value: selected.signed },
          { label: 'Locked', value: selected.locked },
        ]
      : [];
    const stageRects = funnel(
      ctx,
      stages,
      { x: left.x, y: left.y, w: left.w, h: left.h * 0.55 },
      { title: `Cleaning funnel · ${selected?.name ?? ''} (point at a form)` },
    );
    stageRects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `stage:${i}`,
        ...r,
        data: { kind: 'drill', segment: `stage:${stages[i]!.label}|${selected?.name ?? ''}` },
        tooltip: [
          `${stages[i]!.label} · ${selected?.name ?? ''}`,
          `${stages[i]!.value} of ${stages[0]?.value ?? 0} pages`,
          STAGE_GAP[stages[i]!.label]?.hint ?? '',
        ],
      }),
    );
    const rows = list(
      ctx,
      ['Form', 'Pages', 'Entered', 'Verified', 'Locked', 'Attention'],
      perForm.map((p) => ({
        cells: [
          p.name,
          String(p.total),
          String(p.entered),
          String(p.verified),
          String(p.locked),
          String(p.attention),
        ],
        flag: p.attention > 0 ? 'x' : undefined,
        colour: PALETTE.amber,
      })),
      { x: left.x, y: left.y + left.h * 0.57, w: left.w, h: left.h * 0.43 },
      { widths: [0.34, 0.12, 0.14, 0.14, 0.12, 0.14], hoveredIndex: hoveredForm, rowH: 16 },
    );
    rows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `form:${i}`,
        ...r,
        data: { kind: 'drill', segment: `form:${perForm[i]!.name}` },
        tooltip: [
          perForm[i]!.name,
          `${perForm[i]!.attention} pages need attention · open the list`,
        ],
      }),
    );
    const right: Rect = {
      x: area.x + area.w * 0.54,
      y: area.y + 88,
      w: area.w * 0.46,
      h: area.h - 88,
    };
    const prog = this.view.file.dashboards.dataCleaning.progress;
    const cols = lines(
      ctx,
      [
        { values: prog.map((p) => p.enteredPct), colour: PALETTE.teal, label: 'Entered %' },
        { values: prog.map((p) => p.verifiedPct), colour: PALETTE.navy, label: 'Verified %' },
        { values: prog.map((p) => p.signedPct), colour: PALETTE.violet, label: 'Signed %' },
        { values: prog.map((p) => p.lockedPct), colour: PALETTE.forest, label: 'Locked %' },
      ],
      prog.map((p) => p.weekEnding.slice(5)),
      right,
      {
        title: 'Cleaning progress, last 12 weeks',
        max: 100,
        hoveredIndex: hoveredRow(panel, 'week:'),
      },
    );
    cols.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `week:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [
          `Week ending ${prog[i]!.weekEnding}`,
          `entered ${prog[i]!.enteredPct}% · verified ${prog[i]!.verifiedPct}% · signed ${prog[i]!.signedPct}% · locked ${prog[i]!.lockedPct}%`,
        ],
        selectable: false,
      }),
    );
  }
}
